import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { API_URL } from "../../api/api";
import axios from "axios";

import Icon from "../../components/em/Icon";

import { toast } from "../../utils/toast";
import { CalendarDays, Plane, Plus, Trash2, Users as UsersIcon } from "lucide-react";
import { T, PageHeader } from "../../components/em/ui";
import { confirmDialog } from "../../components/em/confirm";
import { todayStr, apiDayStr, addDaysStr, formatDayLong } from "../../utils/localDate";
import "./em-calendar.css";

const EMPTY_EVENT = { id: null, event_type: "", title: "", date: "" };

// Which calendar categories can be edited in place, and the tooltip each gets.
//
// Only the two this page owns a full CRUD surface for are here. The others are
// derived from records that live elsewhere, so there is nothing on this screen
// to save them back to:
//   • leave        — leave_requests supports approve/reject/withdraw, not an
//                    edit; changing a submitted request's dates is not an
//                    operation the API offers (see routes/leaveRoutes.js).
//   • shift        — roster assignments belong to the Shifts page.
//   • birthday     — derived from team_members.dob.
//   • anniversary  — derived from team_members.join_date.
// Both of those last two mean editing an employee record, which is the
// Employees page's job.
const EDITABLE_CATEGORIES = {
  event: "Click to edit this event",
  holiday: "Click to edit this holiday",
};

// Every category visible — the state the calendar always opens in.
const ALL_FILTERS_ON = {
  holidays: true,
  leaves: true,
  events: true,
  birthdays: true,
  anniversaries: true,
};

/* One palette for the grid chips AND the filter pills beneath them.
   Previously each category's colour was written once for the chip and again
   for its pill dot, which is how the Holidays pill ended up orange while the
   holidays on the grid were red, yellow and purple.

   `accent` is the saturated hue: the chip's border, and the pill's dot, which
   needs to read at 8px where a tint would vanish. `ink` is the text. The chip
   background is the accent at low alpha (see `tint`) — never a solid fill, so
   a month's worth of chips stays readable and the cell underneath, including
   the "today" highlight, still shows through. */
const CATEGORY = {
  holiday: { accent: "#9333ea", ink: "#6b21a8" },       // purple
  leave: { accent: "#f97316", ink: "#9a3412" },         // orange
  event: { accent: "#3b82f6", ink: "#1e40af" },         // blue
  birthday: { accent: "#ec4899", ink: "#9d174d" },      // pink
  anniversary: { accent: "#eab308", ink: "#854d0e" },   // light yellow
};

// A hex accent at low opacity. Derived rather than a second hand-picked hex so
// the tint and the dot can never describe different colours.
const tint = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// Chip styling for a category, as FullCalendar's event props.
const chip = (key, alpha = 0.16) => ({
  backgroundColor: tint(CATEGORY[key].accent, alpha),
  borderColor: CATEGORY[key].accent,
  textColor: CATEGORY[key].ink,
});

// Pill order, left to right under the grid.
const FILTER_PILLS = [
  { key: "holidays", label: "Holidays", category: "holiday" },
  { key: "leaves", label: "Leaves", category: "leave" },
  { key: "events", label: "Events", category: "event" },
  { key: "birthdays", label: "Birthdays", category: "birthday" },
  { key: "anniversaries", label: "Anniversaries", category: "anniversary" },
];


const CalendarView = () => {
  const calendarRef = useRef(null);
  const baseURL = `${API_URL}/api/settings/calendar`;
  const leaveurl = `${API_URL}/api`;
  const [events, setEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [todayLeaves, setTodayLeaves] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // holiday | leavetype
  const [showEventModal, setShowEventModal] = useState(false);
  // `id` null → creating; an id → editing that event. One piece of state drives
  // the modal for both, so the two flows can't drift apart.
  const [eventData, setEventData] = useState(EMPTY_EVENT);
  const isEditingEvent = eventData.id != null;

  const closeEventModal = () => {
    setShowEventModal(false);
    setEventData(EMPTY_EVENT);
  };

  const openEventForCreate = () => {
    setEventData(EMPTY_EVENT);
    setShowEventModal(true);
  };

  // Open the modal pre-filled from a calendar event the user clicked.
  const openEventForEdit = (ev) => {
    setEventData({
      id: ev.id,
      event_type: ev.event_type || "",
      title: ev.title || "",
      // event_date is a DATE column, so it arrives as 'YYYY-MM-DD'. Sliced
      // rather than passed through a Date, which would shift the day in any
      // zone behind UTC and silently move the event.
      date: String(ev.event_date || "").slice(0, 10),
    });
    setShowEventModal(true);
  };
  // Local YYYY-MM-DD string (no timezone shift like toISOString would do).
  const toLocalDate = (d) =>
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0");
  const getMaxPastLeaveDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 15);
    return toLocalDate(d);
  };
  const maxPastDate = getMaxPastLeaveDate();

  const [editItem, setEditItem] = useState(null);
  const [deletingHoliday, setDeletingHoliday] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  // Total approved leave DAYS still ahead, from GET /leaves/upcoming-days.
  // Computed server-side on purpose: the count has to exclude each employee's
  // own week-offs (shift-aware) and public holidays, and value a half-day as
  // 0.5 — the same countLeaveDays the leave balances use. The client can't
  // reproduce that without a shift lookup per employee, and a second
  // definition of "leave days" would eventually disagree with the balances.
  const [upcomingLeaveDays, setUpcomingLeaveDays] = useState(null);
  // "company" or "self", straight from the API — it decides the scope from the
  // JWT. Read from there rather than from the local `role`, which this page
  // takes from localStorage.role and which is empty for sessions that only
  // stored the role inside `userdata`.
  const [leaveDaysScope, setLeaveDaysScope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const token = localStorage.getItem("token");
  const role = String(localStorage.getItem("role") || "").toLowerCase();
  const isAdminOrHR = role === "admin" || role === "hr";
  // HR is admin-tier here, so it gets the same calendar controls as Admin.
  const isAdmin = isAdminOrHR;
  // Mirrors the server guard on POST/PUT/DELETE /calendar/events
  // (authorize('admin', 'hr')) — a member clicking an event would only get a
  // 403 from the form, so it stays inert for them.
  const canEditEvents = isAdminOrHR;
  const permissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return [];
    }
  }, []);
  const canApplyLeave =
    permissions.includes("apply_leave") || isAdminOrHR || role === "member";

  const authHeader = useMemo(
    () => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token],
  );
  // Centralised 401 handler — wipes localStorage + redirects to /login.
  const handleAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      localStorage.clear();
      window.location.assign("/login");
      return true;
    }
    return false;
  }, []);

  // Filter toggles are a within-session view control, deliberately NOT
  // persisted: the calendar must always open showing every category. These used
  // to be saved to localStorage, which meant one stray click left a category
  // hidden on every future visit with no hint as to why — a whole set of events
  // simply looked absent. Hiding a category is now always an explicit act the
  // user just performed, and a reload restores the full picture.
  const [filters, setFilters] = useState(ALL_FILTERS_ON);

  // Clear the key the old persisted version left behind, so a stale saved set
  // can't be picked up by anything else (and doesn't linger in the browser).
  useEffect(() => {
    try {
      localStorage.removeItem("cal_filters");
    } catch {
      /* private mode / storage disabled — nothing to clean up */
    }
  }, []);

  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [celebrations, setCelebrations] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [allHolidays, setAllHolidays] = useState([]);
  // Company-wide weekly-offs (admin-editable) — the default the working-day
  // service applies when an employee has no shift assigned.
  const [weeklyOffs, setWeeklyOffs] = useState([]);
  const [savingWeeklyOffs, setSavingWeeklyOffs] = useState(false);
  const [calendarRange, setCalendarRange] = useState({
    start: new Date(),
    end: new Date(),
  });

  // Upcoming holidays, soonest first — derived from the same rows the grid
  // draws so the sidebar can't contradict it.
  //
  // Recurrence is the whole reason this can't be a plain `date >= today`
  // filter. Almost every holiday row is stored ONCE with repeat_annually=1,
  // and the grid expands it into each visible year (expandHolidayOccurrences).
  // The sidebar used to compare that single stored date against today, so it
  // was only ever right during the year the rows happened to be entered for:
  // come January the stored dates are all in the past, the list empties and
  // the count reads 0 while the grid still shows Republic Day two weeks out.
  const upcomingHolidays = useMemo(() => {
    const today = todayStr();
    const thisYear = Number(today.slice(0, 4));
    const out = [];
    for (const h of allHolidays || []) {
      const stored = apiDayStr(h.date);
      if (!stored) continue;
      let occursOn = stored;
      if (h.repeat_annually) {
        // This year's occurrence, or next year's once it has gone by.
        const monthDay = stored.slice(4); // '-09-07'
        occursOn = `${thisYear}${monthDay}`;
        if (occursOn < today) occursOn = `${thisYear + 1}${monthDay}`;
      }
      if (occursOn < today) continue;
      out.push({ ...h, occursOn });
    }
    return out.sort((a, b) => a.occursOn.localeCompare(b.occursOn));
  }, [allHolidays]);

  const modalLabel =
    modalType === "holiday"
      ? "Holiday"
      : modalType === "leavetype"
        ? "Leave Type"
        : modalType === "leave"
          ? "Apply for Leave"
          : "";
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    type: "Public",
    color: "#ff0000",
    repeat_annually: false,
    max_days_per_year: 12,
  });
  const [form, setForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    duration_type: "full_day",
    reason: "",
  });
  const emptyLeaveForm = {
    leave_type_id: "",
    duration_type: "",
    start_date: "",
    end_date: "",
    reason: "",
  };
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      // When switching to half day → set end_date = start_date
      if (name === "duration_type" && value !== "full_day") {
        return {
          ...prev,
          duration_type: value,
          end_date: prev.start_date,
        };
      }

      // When start date changes in half day → sync end date
      if (name === "start_date" && prev.duration_type !== "full_day") {
        return {
          ...prev,
          start_date: value,
          end_date: value,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // existing submit logic
  };
  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    let cancelled = false;

    loadCalendarData();
    fetchTodayLeaves();
    fetchUpcomingLeaveDays();
    fetchCelebrations();
    fetchWeeklyOffs();


    const fetchDepartments = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings/departments`, authHeader);
        // Keep the id alongside the name. team_members.department stores the
        // department's *id* (as a varchar), and that id is what /celebrations
        // and /leaves/all hand back — so the filter has to compare ids. This
        // used to keep names only, which made every comparison ('2' === 'HR')
        // false and emptied the calendar whenever a department was picked.
        if (!cancelled) {
          setDepartments(
            (res.data || []).map((d) => ({ id: String(d.id), name: String(d.name || "").trim() }))
          );
        }
      } catch (err) {
        if (handleAuthError(err)) return;
      }
    };
    fetchDepartments(); // ✅ Call it

    const fetchLeaveTypes = async () => {
      try {
        const res = await axios.get(
          `${leaveurl}/settings/calendar/leave-types`,
          authHeader,
        );
        if (!cancelled) setLeaveTypes(res.data || []);
      } catch (err) {
        if (handleAuthError(err)) return;
        if (!cancelled) setError("Failed to load leave types");
      }
    };

    fetchLeaveTypes();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute calendar events whenever filters or the underlying datasets change.
  useEffect(() => {
    rebuildCalendarEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allHolidays,
    allLeaves,
    events,
    celebrations,
    filters,
    departmentFilter,
    calendarRange,
  ]);

  const fetchTodayLeaves = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leaves/all`, authHeader);
      // Plain string comparison on 'YYYY-MM-DD'. Building Dates out of the
      // DATE columns and then nudging them with setHours mixed UTC midnight
      // (what `new Date('2026-09-11')` is) with local midnight, so the range
      // slid by a day for anyone outside IST — the first or last day of a
      // leave was the wrong side of the boundary.
      const today = todayStr();

      const todayOnLeave = (res.data || []).filter((leave) => {
        if (String(leave.status).toLowerCase() !== "approved") return false;
        const start = apiDayStr(leave.start_date);
        const end = apiDayStr(leave.end_date) || start;
        return start != null && start <= today && today <= end;
      });
      setTodayLeaves(
        todayOnLeave.sort((a, b) =>
          String(a.employee_name || "").localeCompare(String(b.employee_name || ""))
        )
      );
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error("Failed to load today leaves", err);
      setTodayLeaves([]);
    }
  };

  // Scoped server-side from the JWT, exactly like /leaves/all: admin and HR
  // get the company-wide total across every employee, a member gets their own.
  const fetchUpcomingLeaveDays = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leaves/upcoming-days`, authHeader);
      setUpcomingLeaveDays(Number(res.data?.days) || 0);
      setLeaveDaysScope(res.data?.scope || null);
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error("Failed to load upcoming leave days", err);
      setUpcomingLeaveDays(null);
      setLeaveDaysScope(null);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(
        `${leaveurl}/settings/calendar/events`,
        authHeader,
      );
      setEvents(res.data);
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error("Failed to load events", err);
    }
  };

  // Pull birthdays + work anniversaries from the new celebrations endpoint.
  const fetchCelebrations = async () => {
    try {
      const res = await axios.get(`${baseURL}/celebrations`, authHeader);
      setCelebrations(res.data || []);
    } catch (err) {
      if (handleAuthError(err)) return;
      setCelebrations([]);
    }
  };

  // Company-wide weekly-offs default (for the admin control).
  const fetchWeeklyOffs = async () => {
    try {
      const res = await axios.get(`${baseURL}/weekly-offs`, authHeader);
      setWeeklyOffs(res.data?.weekly_offs || []);
    } catch (err) {
      if (handleAuthError(err)) return;
      setWeeklyOffs([]);
    }
  };

  // Admin: toggle a day in the weekly-offs default and persist.
  const toggleWeeklyOff = async (day) => {
    const next = weeklyOffs.includes(day)
      ? weeklyOffs.filter((d) => d !== day)
      : [...weeklyOffs, day];
    setWeeklyOffs(next);
    setSavingWeeklyOffs(true);
    try {
      const res = await axios.put(
        `${baseURL}/weekly-offs`,
        { weekly_offs: next },
        authHeader,
      );
      setWeeklyOffs(res.data?.weekly_offs || next);
    } catch (err) {
      if (handleAuthError(err)) return;
      // Roll back on failure.
      fetchWeeklyOffs();
      toast.error(err?.response?.data?.error || "Failed to save weekly offs");
    } finally {
      setSavingWeeklyOffs(false);
    }
  };

  // Build a recurring holiday's occurrence in the visible calendar range — handles
  // year rollover so historical views still render the holiday.
  const expandHolidayOccurrences = (h, startDate, endDate) => {
    const out = [];
    if (!h.date) return out;
    const m = String(h.date).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return out;
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (!h.repeat_annually) {
      out.push({ ...h, occursOn: h.date });
      return out;
    }
    const startYear = startDate.getFullYear() - 1;
    const endYear = endDate.getFullYear() + 1;
    for (let y = startYear; y <= endYear; y++) {
      const occ = `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      out.push({ ...h, occursOn: occ });
    }
    return out;
  };

  const expandCelebrationOccurrences = (c, startDate, endDate) => {
    const out = [];
    if (!c.month || !c.day) return out;
    const startYear = startDate.getFullYear() - 1;
    const endYear = endDate.getFullYear() + 1;
    for (let y = startYear; y <= endYear; y++) {
      const occ = `${y}-${String(c.month).padStart(2, "0")}-${String(c.day).padStart(2, "0")}`;
      if (c.type === "anniversary" && c.source_year && y < c.source_year)
        continue;
      out.push({ ...c, occursOn: occ, year: y });
    }
    return out;
  };

  const rebuildCalendarEvents = () => {
    const start = calendarRange.start || new Date();
    const end = calendarRange.end || new Date();
    const result = [];
    // `dept` is the department id as stored on team_members (a varchar, and
    // null/'' for the unassigned); `departmentFilter` is the id chosen in the
    // dropdown, or "all". Trimmed on both sides because the stored values are
    // free-text varchars.
    const passDept = (dept) =>
      departmentFilter === "all" ||
      String(dept ?? "").trim() === String(departmentFilter).trim();

    // Holidays (recurring across visible range when repeat_annually=1).
    if (filters.holidays) {
      (allHolidays || []).forEach((h) => {
        expandHolidayOccurrences(h, start, end).forEach((occ) => {
          result.push({
            id: `holiday-${h.id}-${occ.occursOn}`,
            title: h.name,
            start: occ.occursOn,
            allDay: true,
            // One purple for every holiday. The per-holiday `h.color` from
            // the holidays table is deliberately not used here: with it, the
            // grid showed red, yellow and purple holidays under a single
            // "Holidays" legend key, so the key described none of them.
            ...chip("holiday"),
            extendedProps: { category: "holiday", type: h.type, holidayId: h.id },
          });
        });
      });
    }

    // Leaves — approved (green) + pending (amber outline).
    if (filters.leaves) {
      (allLeaves || []).forEach((l) => {
        if (!passDept(l.department)) return;
        const status = String(l.status || "").toLowerCase();
        if (status !== "approved" && status !== "pending") return;
        const isPending = status === "pending";
        const from = apiDayStr(l.start_date);
        const to = apiDayStr(l.end_date);
        if (!from) return;
        result.push({
          id: `leave-${l.id}-${status}`,
          title: `${l.employee_name}${l.leave_type ? ` (${l.leave_type})` : ""}${isPending ? " · Pending" : ""
            }`,
          start: from,
          // FullCalendar treats an all-day event's `end` as EXCLUSIVE, so
          // passing end_date verbatim painted every multi-day leave a day
          // short — a leave through the 11th stopped colouring at the 10th,
          // and the sidebar counts then disagreed with the grid. Hand it the
          // day AFTER the last day of leave.
          end: addDaysStr(to || from, 1),
          allDay: true,
          // Both states orange, per the legend. Pending is the same hue at
          // half the tint — the "· Pending" suffix in the title carries the
          // distinction, so a second colour would only muddy the key.
          ...chip("leave", isPending ? 0.07 : 0.16),
          extendedProps: { category: "leave", status },
        });
      });
    }

    // Personal / company events.
    if (filters.events) {
      (events || []).forEach((e, i) => {
        result.push({
          // The index is part of the id on purpose. calendar_events.id is not
          // AUTO_INCREMENT, so every event inserted without an explicit id is
          // stored as 0 — several rows share it. FullCalendar keys its event
          // store by `id`, so `event-0` collisions made all but one of those
          // events vanish until something forced the array to be re-added (e.g.
          // toggling this filter off and on). Appending the index guarantees a
          // distinct id per row no matter what the database hands us.
          id: `event-${e.id}-${e.event_date}-${i}`,
          title: e.title,
          start: e.event_date,
          allDay: true,
          ...chip("event"),
          extendedProps: { category: "event", eventType: e.event_type, eventId: e.id },
        });
      });
    }

    // Birthdays + anniversaries from celebrations.
    (celebrations || []).forEach((c) => {
      if (c.type === "birthday" && !filters.birthdays) return;
      if (c.type === "anniversary" && !filters.anniversaries) return;
      if (!passDept(c.department)) return;
      expandCelebrationOccurrences(c, start, end).forEach((occ) => {
        const isB = c.type === "birthday";
        result.push({
          id: `${c.id}-${occ.year}`,
          title: isB
            ? `🎂 ${c.name}`
            : `🏆 ${c.name} · ${occ.year - c.source_year} year${occ.year - c.source_year === 1 ? "" : "s"
            }`,
          start: occ.occursOn,
          allDay: true,
          ...chip(isB ? "birthday" : "anniversary"),
          extendedProps: { category: isB ? "birthday" : "anniversary" },
        });
      });
    });

    setCalendarEvents(result);
  };

  // Creates or updates depending on whether the modal was opened from the
  // "Add Event" button or by clicking an existing event.
  const handleSaveEvent = async () => {
    if (!eventData.event_type || !eventData.title || !eventData.date) {
      toast.error("All fields are required");
      return;
    }
    const payload = {
      event_type: eventData.event_type,
      title: eventData.title,
      event_date: eventData.date,
    };
    try {
      const url = `${leaveurl}/settings/calendar/events`;
      if (isEditingEvent) {
        await axios.put(`${url}/${eventData.id}`, payload, authHeader);
      } else {
        await axios.post(url, payload, authHeader);
      }
      toast.success(isEditingEvent ? "Event updated." : "Event added.");
      closeEventModal();
      loadCalendarData();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(
        err?.response?.data?.error ||
          (isEditingEvent ? "Failed to update event" : "Failed to add event"),
      );
    }
  };

  // Delete the personal event currently open for edit. Only reachable from
  // the edit modal, guarded the same way handleSaveEvent's PUT/POST is on the
  // server (authorize('admin', 'hr')).
  const handleDeleteEvent = async () => {
    if (!eventData.id) return;
    const label = eventData.title || "This event";
    if (
      !(await confirmDialog({
        title: "Delete event",
        message: `${label} will be removed from the calendar for everyone. This can't be undone.`,
        confirmLabel: "Delete",
        danger: true,
      }))
    )
      return;

    setDeletingEvent(true);
    try {
      const url = `${leaveurl}/settings/calendar/events`;
      await axios.delete(`${url}/${eventData.id}`, authHeader);
      toast.success("Event deleted.");
      closeEventModal();
      loadCalendarData();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err?.response?.data?.error || "Delete failed");
    } finally {
      setDeletingEvent(false);
    }
  };

  const openApplyLeaveModal = () => {
    setModalType("leave");
    setEditItem(null);
    setFormData({
      start_date: "",
      end_date: "",
      reason: "",
      type: "",
    });
    setShowModal(true);
  };
  const openHolidayModal = () => {
    setModalType("holiday");
    setEditItem(null);
    setFormData({
      name: "",
      date: "",
      type: "Public",
      color: "#ff0000",
      repeat_annually: false,
      max_days_per_year: 12,
    });
    setShowModal(true);
  };

  // Open the holiday modal pre-filled from a holiday the user clicked.
  //
  // `h` is the STORED holiday row, deliberately not the occurrence that was
  // clicked: an annually-repeating holiday paints one box per year, and seeding
  // the form from the clicked box would rewrite the master record's date to
  // whichever year happened to be on screen.
  const openHolidayForEdit = (h) => {
    setModalType("holiday");
    setEditItem(h);
    setFormData({
      name: h.name || "",
      date: String(h.date || "").slice(0, 10),
      type: h.type || "Public",
      // Carried through, not reset to the create-mode default — the colour has
      // no field in this form, so a default here would silently repaint it.
      color: h.color || "#ff0000",
      repeat_annually: !!Number(h.repeat_annually),
      max_days_per_year: 12,
    });
    setShowModal(true);
  };
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      if (!map[ev.event_date]) {
        map[ev.event_date] = [];
      }
      map[ev.event_date].push(ev);
    });
    return map;
  }, [events]);

  const renderDayCell = (dateStr) => {
    const dayEvents = eventsByDate[dateStr] || [];

    return (
      <div className="calendar-cell">
        <div className="date-label">{dateStr.split("-")[2]}</div>

        {dayEvents.map((ev) => (
          <div key={ev.id} className="calendar-event">
            <span className="event-type">{ev.event_type}</span>
            <span className="event-title">{ev.title}</span>
          </div>
        ))}
      </div>
    );
  };

  const openLeaveTypeModal = () => {
    setModalType("leavetype");
    setEditItem(null);
    setFormData({
      name: "",
      date: "",
      type: "Public",
      color: "#ff0000",
      repeat_annually: false,
      max_days_per_year: 12,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (modalType === "holiday") {
        // The modal has always rendered an "Edit Holiday" heading when editItem
        // is set, but this branch only ever POSTed — so saving an edit created a
        // duplicate holiday instead of updating it. PUT closes that gap.
        if (editItem?.id != null) {
          await axios.put(`${baseURL}/holidays/${editItem.id}`, formData, authHeader);
        } else {
          await axios.post(`${baseURL}/holidays`, formData, authHeader);
        }
        toast.success(editItem?.id != null ? "Holiday updated." : "Holiday added.");
      }
      if (modalType === "leavetype") {
        await axios.post(
          `${baseURL}/leave-types`,
          {
            name: formData.name,
            max_days_per_year: formData.max_days_per_year,
            color: formData.color,
          },
          authHeader,
        );
      }
      if (modalType === "leave") {
        const user = JSON.parse(localStorage.getItem("userdata") || "{}");
        if (!user?.id) {
          toast.error("Couldn't identify your account. Please log in again.");
          return;
        }
        await axios.post(
          `${API_URL}/api/leaves/request`,
          {
            user_id: user.id,
            leave_type_id: form.leave_type_id,
            duration_type: form.duration_type,
            start_date: form.start_date,
            end_date: form.end_date,
            reason: form.reason,
          },
          authHeader,
        );
        setForm(emptyLeaveForm);
      }
      setShowModal(false);
      loadCalendarData();
      // Adding or editing a holiday changes the leave-day total too — a
      // holiday inside a leave range stops being charged as leave.
      fetchUpcomingLeaveDays();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err?.response?.data?.error || "Save failed");
    }
  };

  // Delete the holiday currently open for edit. Only reachable from the edit
  // modal, where `editItem` is the STORED row — so a repeating holiday is
  // removed once, not just for the year whose box was clicked.
  const handleDeleteHoliday = async () => {
    if (!editItem?.id) return;
    const label = editItem.name || formData.name || "This holiday";
    if (
      !(await confirmDialog({
        title: "Delete holiday",
        message: `${label} will be removed from the calendar for everyone. This can't be undone.`,
        confirmLabel: "Delete",
        danger: true,
      }))
    )
      return;

    setDeletingHoliday(true);
    try {
      await axios.delete(`${baseURL}/holidays/${editItem.id}`, authHeader);
      toast.success("Holiday deleted.");
      setShowModal(false);
      setEditItem(null);
      loadCalendarData();
      // Same reason handleSave refreshes it: a day that stops being a holiday
      // starts being charged as leave again.
      fetchUpcomingLeaveDays();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err?.response?.data?.error || "Delete failed");
    } finally {
      setDeletingHoliday(false);
    }
  };

  const loadCalendarData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [holidayRes, eventRes, leaveRes] = await Promise.all([
        axios.get(`${baseURL}/holidays`, authHeader),
        axios.get(`${leaveurl}/settings/calendar/events`, authHeader),
        axios.get(`${leaveurl}/leaves/all`, authHeader),
      ]);

      setAllHolidays(holidayRes.data || []);
      setEvents(eventRes.data || []);
      setAllLeaves(leaveRes.data || []);

      // Both sidebar summaries come from elsewhere now: Upcoming Holidays is
      // derived from `allHolidays` (see the useMemo) so it can't drift from
      // what the grid paints, and the leave-day total is computed server-side
      // (fetchUpcomingLeaveDays) so it can't drift from the leave balances.

      // Departments dropdown — derived from leaves data (admin/HR only).
      // const deps = new Set();
      // (leaveRes.data || []).forEach(
      //   (l) => l.department && deps.add(l.department),
      // );
      // setDepartments(Array.from(deps).sort());
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error("Failed to load calendar data", err);
      setError(err?.response?.data?.error || "Failed to load calendar data.");
      setCalendarEvents([]);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- HEADER ACTIONS ---------------- */
  const goPrev = () => calendarRef.current.getApi().prev();
  const goNext = () => calendarRef.current.getApi().next();
  const goToday = () => calendarRef.current.getApi().today();

  /* ---------------- UI ---------------- */
  return (
    <div className="calendar-page em-calendar" style={{ maxWidth: 1440, margin: "0 auto", padding: "0 32px 48px" }}>
      {/* HEADER */}
      <PageHeader icon={CalendarDays} title="Calendar" subtitle="View leaves, holidays and company events" />

      {error && (
        <div role="alert" className="cal-error-banner">
          ⚠ {error}
        </div>
      )}
      {loading && <div className="cal-loading">Loading calendar…</div>}

      <div className="calendar-layout">
        {/* CALENDAR */}
        <div className="calendar-wrapper">
          <div className="card">
            <FullCalendar
              ref={calendarRef}
              // No listPlugin: the List view was removed, and leaving the
              // plugin registered would keep `listMonth` reachable through
              // the API (and ship its bundle) for a view nothing can open.
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: "prev,title,next",
                right: "dayGridMonth,timeGridWeek,timeGridDay,customToday",
              }}
              customButtons={{
                customToday: {
                  text: "Today",
                  click: () => {
                    calendarRef.current?.getApi().today();
                  },
                },
              }}
              titleFormat={{ month: "long", year: "numeric" }}
              // Day view's default header is just the weekday name ("Wednesday")
              // since FullCalendar assumes the title alone disambiguates the
              // date. Matching Week view's "Sun 9/6" format here keeps that
              // context on screen instead of requiring a glance at the title.
              views={{
                timeGridDay: {
                  dayHeaderFormat: { weekday: "short", month: "numeric", day: "numeric" },
                },
              }}
              buttonText={{
                today: "Today",
                month: "Month",
                week: "Week",
                day: "Day",
              }}
              events={calendarEvents}
              height="auto"
              // Track the visible range so recurring/celebration occurrences render only
              // within (or near) what's on screen. Avoids generating 100 years of events.
              datesSet={(arg) => {
                setCalendarRange({ start: arg.start, end: arg.end });
              }}
              // Clicking an item opens its own editor, populated. Only the two
              // categories this page owns are editable — see EDITABLE_CATEGORIES
              // for why the derived ones are not.
              eventClick={(info) => {
                const { category, eventId, holidayId } = info.event.extendedProps;
                if (!canEditEvents) return;
                if (category === "event") {
                  const source = (events || []).find(
                    (e) => String(e.id) === String(eventId),
                  );
                  if (source) openEventForEdit(source);
                  return;
                }
                if (category === "holiday") {
                  const source = (allHolidays || []).find(
                    (h) => String(h.id) === String(holidayId),
                  );
                  if (source) openHolidayForEdit(source);
                }
              }}
              eventDidMount={(info) => {
                const cat = info.event.extendedProps.category;
                if (cat) info.el.classList.add(`fc-${cat}`);
                if (info.event.extendedProps.status === "pending") {
                  info.el.classList.add("fc-leave-pending");
                }
                // A day column fits ~18 characters, so most labels are
                // ellipsised on the grid ("Bertin Dcruz (Annual…"). The native
                // tooltip carries the full text, plus the edit hint the
                // editable categories used to show on its own.
                //
                // Set on every chip rather than only the ones measuring as
                // clipped: whether a label clips depends on the column width,
                // which changes with the window and the number of events in
                // the day, so a measurement taken here — during FullCalendar's
                // render, before the final layout — would be both unreliable
                // and stale the moment the window is resized.
                const editable = canEditEvents && EDITABLE_CATEGORIES[cat];
                info.el.title = editable
                  ? `${info.event.title}\n${EDITABLE_CATEGORIES[cat]}`
                  : info.event.title;
                // Only the editable ones advertise themselves as clickable.
                if (editable) info.el.classList.add("fc-event-editable");
              }}
            />

            <div className="button-section">
              {/* Filter pills — toggle visibility of each event category. */}
              <div className="cal-filter-pills">
                {FILTER_PILLS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`cal-filter-pill ${filters[f.key] ? "is-on" : ""}`}
                    // Same accent the category's chips use — one source, so a
                    // recoloured category can't leave its pill behind.
                    style={{ "--pill-color": CATEGORY[f.category].accent }}
                    onClick={() =>
                      setFilters((p) => ({ ...p, [f.key]: !p[f.key] }))
                    }
                    aria-pressed={filters[f.key]}
                  >
                    <span className="cal-filter-dot" />
                    {f.label}
                  </button>
                ))}
              </div>

              {isAdminOrHR && departments.length > 0 && (
                <div className="cal-dept-filter">

                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                  >
                    <option value="all">All departments</option>
                    {/* value is the id (what the data carries), label is the
                        name (what a human reads). */}
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Weekly-offs default — admin only. This is the company-wide
                fallback the working-day service (leaves/attendance/payroll)
                applies when an employee has no shift assigned. */}
            {isAdmin && (
              <div className="cal-weekoffs">
                <span className="cal-weekoffs-label">
                  Default weekly offs
                  {savingWeeklyOffs ? " · saving…" : ""}
                </span>
                <div className="cal-weekoffs-days">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => {
                    const on = weeklyOffs.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        className={`cal-weekoff-day ${on ? "is-off" : ""}`}
                        aria-pressed={on}
                        title={on ? `${d} is a weekly off` : `${d} is a working day`}
                        onClick={() => toggleWeeklyOff(d)}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>


        </div>

        {showEventModal && (
          <div className="modal-backdrop">
            <div className="modal">
              <h3>
                {isEditingEvent ? "Edit Event" : "Add Personal Event"}
                <div className="close-btn" onClick={closeEventModal}>
                  <Icon name="close" color="#f54b01" />
                </div>
              </h3>
              <p>
                {isEditingEvent
                  ? "Update the type, title or date of this event."
                  : "Add a birthday,anniversary, or other personal event "}
              </p>
              {/* Event Type */}
              <div className="form-item">
                <label>Event Type</label>
                <select
                  value={eventData.event_type}
                  onChange={(e) =>
                    setEventData({ ...eventData, event_type: e.target.value })
                  }
                >
                  <option value="">Select type</option>
                  <option value="Company Event">Company Event</option>
                  <option value="Meeting">Meeting</option>
                  <option value="Personal">Personal</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Title */}
              <div className="form-item">
                <label>Title</label>
                <input
                  type="text"
                  placeholder="Event title"
                  value={eventData.title}
                  onChange={(e) =>
                    setEventData({ ...eventData, title: e.target.value })
                  }
                />
              </div>

              {/* Date */}
              <div className="form-item">
                <label>Date</label>
                <input
                  type="date"
                  value={eventData.date}
                  onChange={(e) =>
                    setEventData({ ...eventData, date: e.target.value })
                  }
                />
              </div>

              {/* Actions */}
              <div className="modal-actions">
                {/* Editing an existing event is the only place a delete
                    belongs — and only for admin/HR, mirroring the server
                    guard on DELETE /calendar/events/:id. */}
                {isEditingEvent && isAdmin && (
                  <button
                    type="button"
                    className="cal-modal-delete"
                    onClick={handleDeleteEvent}
                    disabled={deletingEvent}
                  >
                    <Trash2 size={14} />
                    {deletingEvent ? "Deleting…" : "Delete"}
                  </button>
                )}
                <button onClick={closeEventModal}>Cancel</button>

                <button className="orng-fill" onClick={handleSaveEvent}>
                  {isEditingEvent ? "Save Changes" : "Add Event"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal-backdrop">
            <div className="modal">
              <div className="modal-body-calendar">
                <h3>
                  {editItem ? `Edit ${modalLabel}` : ` ${modalLabel}`}
                  <div
                    className="close-btn"
                    onClick={() => setShowModal(false)}
                  >
                    <Icon name="close" color="#f54b01" />
                  </div>
                </h3>
                <p>Submit a new leave request for approval</p>

                {/* NAME – only for Holiday & Leave Type */}
                {(modalType === "holiday" || modalType === "leavetype") && (
                  <div className="form-item">
                    <label>Name</label>
                    <input
                      type="text"
                      value={formData.name || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>
                )}

                {/* HOLIDAY FIELDS */}
                {modalType === "holiday" && (
                  <>
                    <div className="form-item">
                      <label>Date</label>
                      <input
                        type="date"
                        value={formData.date || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, date: e.target.value })
                        }
                      />
                    </div>

                    <div className="form-item">
                      <label>Type</label>
                      <select
                        value={formData.type || "Public"}
                        onChange={(e) =>
                          setFormData({ ...formData, type: e.target.value })
                        }
                      >
                        <option value="Public">Public</option>
                        <option value="Optional">Optional</option>
                      </select>
                    </div>

                    <div className="form-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.repeat_annually || false}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              repeat_annually: e.target.checked,
                            })
                          }
                        />
                        Repeat Annually
                      </label>
                    </div>
                  </>
                )}

                {/* LEAVE TYPE FIELDS */}
                {modalType === "leavetype" && (
                  <div className="form-item">
                    <label>Max Days Per Year</label>
                    <input
                      type="number"
                      value={formData.max_days_per_year || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_days_per_year: e.target.value,
                        })
                      }
                    />
                  </div>
                )}

                {/* APPLY LEAVE FIELDS */}
                {modalType === "leave" && (
                  <form className="form-body">
                    {error && <div className="alert alert-error">{error}</div>}

                    <div className="form-item">
                      <label>Leave Type</label>
                      <select
                        name="leave_type_id"
                        value={form.leave_type_id}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select leave type</option>
                        {leaveTypes.map((lt) => (
                          <option key={lt.id} value={lt.id}>
                            {lt.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-item">
                      <label>Duration Type</label>
                      <select
                        name="duration_type"
                        value={form.duration_type}
                        onChange={handleChange}
                        required
                      >
                        <option value="full_day">Full Day</option>
                        <option value="half_day_first">
                          Half Day – First Half
                        </option>
                        <option value="half_day_second">
                          Half Day – Second Half
                        </option>
                      </select>
                    </div>
                    <div className="form-item">
                      <label>Start Date</label>
                      <input
                        type="date"
                        name="start_date"
                        value={form.start_date}
                        onChange={handleChange}
                        min={maxPastDate}
                        required
                      />
                    </div>
                    {form.duration_type === "full_day" && (
                      <div className="form-item">
                        <label>End Date</label>
                        <input
                          type="date"
                          name="end_date"
                          value={form.end_date}
                          onChange={handleChange}
                          min={maxPastDate}
                          required
                        />
                      </div>
                    )}

                    <div className="form-item">
                      <label>Reason (optional)</label>
                      <textarea
                        name="reason"
                        value={form.reason}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Enter reason for leave"
                      />
                    </div>
                  </form>
                )}

                {/* COLOR — Leave Type only.
                    Holidays no longer offer one: every holiday on the grid is
                    drawn in the category purple (see CATEGORY), so the picker
                    controlled nothing a user could see. `holidays.color` is
                    still carried through on save, so existing values survive
                    an edit rather than being reset. Leave types keep theirs —
                    leave_types.color is read by the balances and the leave
                    views. */}
                {modalType === "leavetype" && (
                  <div className="form-item">
                    <label>Color</label>
                    <input
                      type="color"
                      value={formData.color || "#ff0000"}
                      onChange={(e) =>
                        setFormData({ ...formData, color: e.target.value })
                      }
                    />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                {/* Editing an existing holiday is the only place a delete
                    belongs — and only for admin/HR, mirroring the server guard
                    on DELETE /calendar/holidays/:id. */}
                {modalType === "holiday" && editItem?.id != null && isAdmin && (
                  <button
                    type="button"
                    className="cal-modal-delete"
                    onClick={handleDeleteHoliday}
                    disabled={deletingHoliday}
                  >
                    <Trash2 size={14} />
                    {deletingHoliday ? "Deleting…" : "Delete"}
                  </button>
                )}
                <button onClick={() => setShowModal(false)}>Cancel</button>
                <button className="orng-fill" onClick={handleSave}>
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SIDEBAR */}
        <div className="calendar-sidebar" style={emCal.sidebar}>
          {/* Quick Actions */}
          <div style={emCal.card}>
            <div style={emCal.cardTitle}>Quick Actions</div>
            {canApplyLeave && (
              <button onClick={openApplyLeaveModal} style={emCal.primaryBtn}>
                <Plane size={15} /> Apply for Leave
              </button>
            )}
            <div style={emCal.btnRow}>
              {isAdmin && (
                <button onClick={() => openHolidayModal()} style={emCal.secBtn}><Plus size={14} /> Add Holiday</button>
              )}
              {/* openEventForCreate resets the form first — otherwise an id left
                  over from a previous edit would make this "add" silently
                  update that event instead. */}
              {isAdminOrHR && (
                <button onClick={openEventForCreate} style={emCal.secBtn}><Plus size={14} /> Add Event</button>
              )}
            </div>
          </div>

          {/* Counts */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={emCal.statTile}>
              <div style={{ font: `800 24px ${T.font}`, color: T.fg1 }}>
                {upcomingLeaveDays == null ? "—" : upcomingLeaveDays}
              </div>
              {/* "Leave Days", not "Leaves": it totals days, not requests, and
                  a bare "9" under "Upcoming Leaves" reads as nine requests. */}
              <div style={{ font: `500 11px ${T.font}`, color: T.fg5, marginTop: 2 }}>
                Upcoming Leave Days{leaveDaysScope === "company" ? " · All Staff" : ""}
              </div>
            </div>
            <div style={emCal.statTile}>
              <div style={{ font: `800 24px ${T.font}`, color: T.fg1 }}>{upcomingHolidays.length}</div>
              <div style={{ font: `500 11px ${T.font}`, color: T.fg5, marginTop: 2 }}>Upcoming Holidays</div>
            </div>
          </div>

          {/* Upcoming Holidays */}
          <div style={emCal.card}>
            <div style={emCal.cardTitleRow}>
              <span style={{ ...emCal.iconTile }}><CalendarDays size={15} color={T.orange} /></span>
              <span style={{ font: `700 14px ${T.font}`, color: T.fg1, flex: 1 }}>Upcoming Holidays</span>
              <span style={emCal.countChip}>{upcomingHolidays.length}</span>
            </div>
            {upcomingHolidays.length ? (
              <>
                {upcomingHolidays.slice(0, 5).map((h) => (
                  <div key={`${h.id}-${h.occursOn}`} style={emCal.listRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>{h.name}</div>
                      {/* occursOn, not the stored date — a recurring holiday's
                          row still carries the year it was first entered. */}
                      <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 1 }}>{formatDayLong(h.occursOn)}</div>
                    </div>
                    <span style={{ ...emCal.badge, ...(h.type === "Optional" ? { background: T.bgMuted, color: T.fg5 } : {}) }}>{h.type || "Public"}</span>
                  </div>
                ))}
                {/* The chip above counts them all, so say so rather than
                    leaving the extras silently missing from the list. */}
                {upcomingHolidays.length > 5 && (
                  <div style={{ font: `500 11px ${T.font}`, color: T.fg7, paddingTop: 10 }}>
                    +{upcomingHolidays.length - 5} more
                  </div>
                )}
              </>
            ) : (
              <p style={{ font: `400 13px ${T.font}`, color: T.fg7, textAlign: "center", padding: "12px 0 4px" }}>No upcoming holidays</p>
            )}
          </div>

          {/* On Leave Today */}
          <div style={emCal.card}>
            <div style={emCal.cardTitleRow}>
              <span style={{ ...emCal.iconTile }}><UsersIcon size={15} color={T.orange} /></span>
              <span style={{ font: `700 14px ${T.font}`, color: T.fg1, flex: 1 }}>On Leave Today</span>
            </div>
            {todayLeaves.length ? (
              todayLeaves.map((l) => (
                <div key={l.id || `${l.user_id}-${l.start_date}`} style={emCal.listRow}>
                  <span style={{ width: 28, height: 28, borderRadius: 9999, background: T.gradient, flexShrink: 0 }} />
                  <span style={{ flex: 1, font: `600 13px ${T.font}`, color: T.fg1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.employee_name}</span>
                  <span style={{ ...emCal.badge, background: T.purple50, color: T.purple }}>{l.leave_type}</span>
                </div>
              ))
            ) : (
              <div style={{ textAlign: "center", padding: "16px 0 4px" }}>
                <b style={{ font: `600 13px ${T.font}`, color: T.fg1, display: "block" }}>No one on leave</b>
                <p style={{ margin: "4px 0 0", font: `400 12px ${T.font}`, color: T.fg7 }}>All employees are available today</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const emCal = {
  sidebar: { display: "flex", flexDirection: "column", gap: 16 },
  card: { background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowSm, padding: "18px 20px" },
  cardTitle: { font: `700 14px ${T.font}`, color: T.fg1, marginBottom: 12 },
  cardTitleRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  iconTile: { width: 30, height: 30, borderRadius: 8, background: T.orange50, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  countChip: { padding: "2px 9px", borderRadius: 9999, background: T.bgMuted, color: T.fg5, font: `600 11px ${T.font}` },
  primaryBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "11px 16px", border: 0, borderRadius: 10, cursor: "pointer", background: T.gradient, color: "#fff", font: `600 13px ${T.font}`, boxShadow: T.shadowBrand, marginBottom: 10 },
  btnRow: { display: "flex", gap: 8, marginBottom: 10 },
  secBtn: { flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer", background: "var(--em-bg-card)", color: T.fg4, font: `500 12.5px ${T.font}` },
  statTile: { flex: 1, background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowSm, padding: "16px 18px" },
  listRow: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${T.divider}` },
  badge: { marginLeft: "auto", padding: "2px 9px", borderRadius: 9999, background: T.orange50, color: T.orange, font: `600 10px ${T.font}`, whiteSpace: "nowrap", flexShrink: 0 },
};

export default CalendarView;
